import { datadogLogs } from "@datadog/browser-logs";
import {
  parseFetchRequestBody,
  parseFetchRequestInput,
} from "./parseFetchBody";

export interface DatadogAdditionalData {
  request?: {
    url: string;
    method?: string;
    headers?: Record<string, string> | undefined;
    body?: unknown;
    operationName?: string;
    variables?: Record<string, unknown>;
    query?: string;
    parseError?: unknown;
  };
  response?: {
    headers?: Record<string, string>;
    ok?: boolean;
    redirected?: boolean;
    status?: number;
    statusText?: string;
    type?: ResponseType;
    url?: string;
    data?: unknown;
    parseError?: unknown;
  };
  error?: unknown;
  parseError?: unknown;
}

interface GraphqlBody {
  operationName: string;
  variables: Record<string, string>;
  query: string;
}

export const extractFetchHeaders = (
  headers?: HeadersInit
): Record<string, string> | undefined => {
  if (!headers) {
    return undefined;
  }
  const headersMap = {};
  const headersIterable =
    Array.isArray(headers) || headers instanceof Headers
      ? headers
      : Object.entries(headers);

  for (const [key, value] of headersIterable) {
    if (key.toLowerCase() === "authorization") {
      continue;
    }
    headersMap[key] = value;
  }
  return headersMap;
};

const handleParseError = (parseError: unknown) => {
  if (parseError instanceof Error) {
    datadogLogs.logger.warn(
      "There was error with extracting request data from this fetch request to track in datadog",
      null,
      parseError
    );
  } else {
    datadogLogs.logger.warn(
      "There was error with extracting request data from this fetch request to track in datadog",
      null,
      new Error(`UnknownError ${parseError}`)
    );
  }
};

const extractFetchRequestData = async (
  input: URL | RequestInfo,
  init?: RequestInit
) => {
  try {
    if (typeof input === "string" || input instanceof URL) {
      const method = init?.method ?? "GET";
      const headers = extractFetchHeaders(init?.headers);
      const body = await parseFetchRequestBody(init?.body, headers);
      return { method, headers, body };
    }
    const method = input.method;
    const headers = extractFetchHeaders(input.headers);
    const body = await parseFetchRequestInput(input.clone(), headers);
    return { method, headers, body };
  } catch (parseError: unknown) {
    handleParseError(parseError);
    return {
      parseError,
    };
  }
};

async function parseResponse(
  response?: Response,
  options?: { skipBody?: boolean }
): Promise<DatadogAdditionalData["response"]> {
  if (!response) {
    return undefined;
  }

  try {
    let data: unknown = undefined;

    // we shouldn't have response with bodyUsed here,
    // but calling clone() on response with bodyUsed would throw an error
    if (!response.bodyUsed && options?.skipBody !== true) {
      data = await parseResponseData(response);
    }

    return {
      headers: extractFetchHeaders(response.headers),
      ok: response.ok,
      redirected: response.redirected,
      status: response.status,
      statusText: response.statusText,
      type: response.type,
      url: response.url,
      data,
    };
  } catch (parseError) {
    handleParseError(parseError);

    return {
      parseError,
    };
  }
}

const parseResponseData = async (response: Response) => {
  // don't track clerk responses which might contain jwt token
  if (response.url.includes(process.env.NEXT_PUBLIC_CLERK_HOSTNAME)) {
    return "REDACTED";
  }

  if (
    response.headers
      .get("content-type")
      ?.toLowerCase()
      .includes("application/json")
  ) {
    return response.clone().json();
  }
  return response.clone().text();
};

const isIntrospectionQuery = (operationName: string) => {
  return operationName === "IntrospectionQuery";
};

const getRequestUrl = (input: URL | RequestInfo) => {
  if (typeof input === "string") {
    return input;
  }

  if (input instanceof URL) {
    return input.toString();
  }

  return input.url;
};

/**
 * Shallow clone error to avoid referencing same error in additionalDatadogData
 *
 * error = { additionalDatadogData: { error }}
 *   ^                                   ^
 *   |                                   |
 *   |-----------------------------------|---- same error without cloning
 */
function shallowCloneError(original?: Record<string, unknown>) {
  if (!original) {
    return undefined;
  }

  if (original instanceof Error) {
    const cloned = new (original.constructor as ErrorConstructor)(
      original.message
    );
    cloned.name = original.name;
    if (original.stack) {
      cloned.stack = original.stack;
    }
    for (const prop of Object.getOwnPropertyNames(original)) {
      if (["name", "message", "stack"].includes(prop)) {
        continue;
      }
      cloned[prop] = original[prop];
    }
    return cloned;
  }

  return { ...original };
}

export const extractFetchData = async (
  input: URL | RequestInfo,
  init: RequestInit | undefined,
  { response, error }: { response?: Response; error?: Record<string, unknown> }
): Promise<DatadogAdditionalData | undefined> => {
  try {
    const url = getRequestUrl(input);
    if (url.includes(process.env.NEXT_PUBLIC_DATADOG_SITE)) {
      // don't track datadog requests
      return undefined;
    }
    const requestData = await extractFetchRequestData(input, init);

    if (url.includes("graphql")) {
      const gqlBody = requestData.body as GraphqlBody | undefined;
      const operationName = gqlBody?.operationName;
      return {
        request: {
          url,
          operationName,
          variables: gqlBody?.variables,
          query: gqlBody?.query,
          ...requestData,
        },
        response: await parseResponse(response, {
          // skip parsing body for introspection queries
          // which have too much data with no usefulnes
          skipBody: isIntrospectionQuery(operationName),
        }),
        error: shallowCloneError(error),
      };
    }
    return {
      request: {
        url,
        ...requestData,
      },
      response: await parseResponse(response),
      error: shallowCloneError(error),
    };
  } catch (parseError) {
    handleParseError(parseError);

    return {
      parseError,
    };
  }
};
